import { PartImageNarrowTarget } from '@/api/api';
import {
  PartImageCandidate,
  PartImageCharacteristicQuestion,
  PartImageItemTypeOption,
  PartImageTolerance,
} from '@/api/generatedApi';
import { ApiErrorAlert } from '@/components/Errors/ApiErrorAlert';
import { useGetPartImageCharacteristicQuestions } from '@/hooks/api/part-finder-image-search/useGetPartImageCharacteristicQuestions';
import { useNarrowPartImageCandidates } from '@/hooks/api/part-finder-image-search/useNarrowPartImageCandidates';
import {
  MIN_ITEM_TYPE_SEARCH_LENGTH,
  useSearchPartImageItemTypes,
} from '@/hooks/api/part-finder-image-search/useSearchPartImageItemTypes';
import { getApiError } from '@/types/apiError';
import SearchIcon from '@mui/icons-material/Search';
import {
  Autocomplete,
  Box,
  Button,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { ReactNode, useState } from 'react';
import { parseInches } from './partImageInches';
import { DEFAULT_TOLERANCE_CHOICE, matchedRange, TOLERANCE_CHOICES } from './partImageTolerance';

const Panel = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '8px',
  [theme.breakpoints.up('md')]: { maxHeight: '100%' },
}));

const PanelBody = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
  padding: '16px',
  [theme.breakpoints.up('md')]: { flex: '1 1 auto', minHeight: 0, overflowY: 'auto' },
}));

const PanelFooter = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  padding: '12px 16px 16px',
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const SectionLabel = ({ children }: { children: string }) => (
  <Typography variant='overline' color='text.secondary' sx={{ lineHeight: 1.5 }}>
    {children}
  </Typography>
);

type Answers = Record<string, string>;

const toRequest = (questions: PartImageCharacteristicQuestion[], answers: Answers) => ({
  dimensions: questions
    .filter(question => question.kind === 'dimension')
    .flatMap(question => {
      const inches = parseInches(answers[question.code] ?? '');
      return inches === undefined ? [] : [{ code: question.code, inches }];
    }),
  texts: questions
    .filter(question => question.kind === 'text' && answers[question.code]?.trim())
    .map(question => ({ code: question.code, contains: answers[question.code].trim() })),
});

const unreadableSizes = (questions: PartImageCharacteristicQuestion[], answers: Answers): boolean =>
  questions.some(
    question =>
      question.kind === 'dimension' &&
      answers[question.code]?.trim() &&
      parseInches(answers[question.code]) === undefined
  );

const sizeHelper = (value: string, tolerance: PartImageTolerance): string | undefined => {
  if (value.trim().length === 0) return undefined;
  const inches = parseInches(value);
  return inches === undefined ? 'Use inches, like 0.75 or 3/4' : `Matches ${matchedRange(inches, tolerance)}`;
};

const QuestionField = ({
  question,
  value,
  tolerance,
  disabled,
  onChange,
}: {
  question: PartImageCharacteristicQuestion;
  value: string;
  tolerance: PartImageTolerance;
  disabled: boolean;
  onChange: (value: string) => void;
}) => {
  if (question.kind === 'text') {
    return (
      <Autocomplete
        freeSolo
        disabled={disabled}
        options={question.options}
        inputValue={value}
        onInputChange={(_event, next) => onChange(next)}
        renderInput={parameters => (
          <TextField {...parameters} label={question.label} size='small' placeholder='Optional' fullWidth />
        )}
      />
    );
  }
  const unreadable = value.trim().length > 0 && parseInches(value) === undefined;
  return (
    <TextField
      label={`${question.label} (in)`}
      size='small'
      fullWidth
      value={value}
      disabled={disabled}
      error={unreadable}
      helperText={sizeHelper(value, tolerance)}
      onChange={event => onChange(event.target.value)}
      slotProps={{ htmlInput: { inputMode: 'decimal', 'data-testid': `part-image-measure-${question.code}` } }}
    />
  );
};

interface QuestionSectionProperties {
  title: string;
  caption: string;
  questions: PartImageCharacteristicQuestion[];
  testId: string;
  control?: ReactNode;
  answers: Answers;
  tolerance: PartImageTolerance;
  disabled: boolean;
  onAnswer: (code: string, value: string) => void;
}

const QuestionSection = ({
  title,
  caption,
  questions,
  testId,
  control,
  answers,
  tolerance,
  disabled,
  onAnswer,
}: QuestionSectionProperties) => (
  <Stack gap='12px' data-testid={testId}>
    <Box>
      <SectionLabel>{title}</SectionLabel>
      <Typography variant='caption' color='text.secondary' component='div'>
        {caption}
      </Typography>
    </Box>
    {control}
    {questions.map(question => (
      <QuestionField
        key={question.code}
        question={question}
        value={answers[question.code] ?? ''}
        tolerance={tolerance}
        disabled={disabled}
        onChange={value => onAnswer(question.code, value)}
      />
    ))}
  </Stack>
);

const ToleranceChoice = ({
  label,
  disabled,
  onChoose,
}: {
  label: string;
  disabled: boolean;
  onChoose: (label: string) => void;
}) => (
  <TextField
    select
    size='small'
    fullWidth
    label='Tolerance'
    value={label}
    disabled={disabled}
    onChange={event => onChoose(event.target.value)}
    slotProps={{ htmlInput: { 'data-testid': 'part-image-tolerance' } }}
  >
    {TOLERANCE_CHOICES.map(choice => (
      <MenuItem key={choice.label} value={choice.label}>
        {choice.label}
      </MenuItem>
    ))}
  </TextField>
);

const itemTypeKey = (itemType: PartImageItemTypeOption) => `${itemType.itemName}|${itemType.federalSupplyClass}`;

const itemTypeLabel = (itemType: PartImageItemTypeOption) =>
  `${itemType.itemName} (FSC ${itemType.federalSupplyClass})`;

const OtherItemTypeSearch = ({ onChoose }: { onChoose: (itemType: PartImageItemTypeOption) => void }) => {
  const [search, setSearch] = useState('');
  const { data = [], isFetching } = useSearchPartImageItemTypes(search);
  return (
    <Autocomplete
      size='small'
      forcePopupIcon={false}
      options={search.trim().length < MIN_ITEM_TYPE_SEARCH_LENGTH ? [] : data}
      loading={isFetching}
      filterOptions={options => options}
      getOptionLabel={itemTypeLabel}
      renderOption={(properties, option) => (
        <li {...properties} key={itemTypeKey(option)}>
          <Stack>
            <Typography variant='body2'>{option.itemName}</Typography>
            <Typography variant='caption' color='text.secondary'>
              FSC {option.federalSupplyClass} · {option.partCount.toLocaleString()} parts
            </Typography>
          </Stack>
        </li>
      )}
      noOptionsText={
        search.trim().length < MIN_ITEM_TYPE_SEARCH_LENGTH
          ? `Type at least ${MIN_ITEM_TYPE_SEARCH_LENGTH} letters`
          : 'No FLIS item type matches'
      }
      inputValue={search}
      onInputChange={(_event, next, reason) => reason !== 'reset' && setSearch(next)}
      // eslint-disable-next-line unicorn/no-null -- MUI keeps a controlled Autocomplete empty only with null
      value={null}
      onChange={(_event, itemType) => {
        if (!itemType) return;
        setSearch('');
        onChoose({ itemName: itemType.itemName, federalSupplyClass: itemType.federalSupplyClass });
      }}
      renderInput={parameters => (
        <TextField
          {...parameters}
          placeholder='Not listed? Search FLIS item types'
          slotProps={{
            input: {
              ...parameters.InputProps,
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon fontSize='small' />
                </InputAdornment>
              ),
            },
          }}
        />
      )}
    />
  );
};

const ItemTypeChoice = ({
  itemTypes,
  current,
  onChoose,
}: {
  itemTypes: PartImageItemTypeOption[];
  current?: PartImageItemTypeOption;
  onChoose: (itemType: PartImageItemTypeOption) => void;
}) => (
  <Stack gap='8px' data-testid='part-image-item-types'>
    <SectionLabel>Item type</SectionLabel>
    {itemTypes.length > 0 && (
      <RadioGroup
        value={current ? itemTypeKey(current) : ''}
        onChange={event => {
          const chosen = itemTypes.find(itemType => itemTypeKey(itemType) === event.target.value);
          if (chosen) onChoose(chosen);
        }}
      >
        {itemTypes.map(itemType => (
          <FormControlLabel
            key={itemTypeKey(itemType)}
            value={itemTypeKey(itemType)}
            control={<Radio size='small' />}
            label={
              <Typography variant='body2'>
                {itemType.itemName}{' '}
                <Typography component='span' variant='caption' color='text.secondary'>
                  FSC {itemType.federalSupplyClass}
                </Typography>
              </Typography>
            }
          />
        ))}
      </RadioGroup>
    )}
    <OtherItemTypeSearch onChoose={onChoose} />
  </Stack>
);

const FieldSkeletons = ({ count }: { count: number }) => (
  <Stack gap='12px' data-testid='part-image-narrow-fields-loading'>
    <Skeleton variant='text' width='35%' />
    {Array.from({ length: count }, (_, index) => (
      <Skeleton key={index} variant='rounded' height={40} />
    ))}
  </Stack>
);

const NarrowSkeleton = () => (
  <Panel data-testid='part-image-narrow-loading'>
    <PanelBody>
      <Stack gap='4px'>
        <Skeleton variant='text' width='70%' sx={{ fontSize: '1.1rem' }} />
        <Skeleton variant='text' width='45%' />
      </Stack>
      <Stack gap='8px'>
        <Skeleton variant='text' width='25%' />
        <Skeleton variant='text' width='60%' />
        <Skeleton variant='text' width='50%' />
        <Skeleton variant='rounded' height={40} />
      </Stack>
      <FieldSkeletons count={3} />
    </PanelBody>
    <PanelFooter>
      <Skeleton variant='rounded' height={36} />
    </PanelFooter>
  </Panel>
);

const matchSummary = (total: number, shown: number): string => {
  if (total === 0) {
    return 'No parts of this type match. Check the sizes are in inches, or leave a size or the material blank.';
  }
  if (shown >= total) return total === 1 ? '1 part matches.' : `${total} parts match, all listed.`;
  return `${total.toLocaleString()} parts match; the ${shown} closest are listed.`;
};

const MatchSummary = ({ total, shown }: { total: number; shown: number }) => (
  <Typography variant='body2' color='text.secondary' data-testid='part-image-narrow-summary'>
    {matchSummary(total, shown)}
  </Typography>
);

export interface NarrowByMeasurementsProperties {
  identificationId: string;
  target?: PartImageNarrowTarget;
  onNarrowed?: (candidates: PartImageCandidate[]) => void;
}

export const NarrowByMeasurements = ({ identificationId, target, onNarrowed }: NarrowByMeasurementsProperties) => {
  const [answers, setAnswers] = useState<Answers>({});
  const [chosenItemType, setChosenItemType] = useState<PartImageItemTypeOption>();
  const [toleranceLabel, setToleranceLabel] = useState(DEFAULT_TOLERANCE_CHOICE.label);
  const { data, error, isPlaceholderData } = useGetPartImageCharacteristicQuestions(
    identificationId,
    chosenItemType,
    target
  );
  const narrow = useNarrowPartImageCandidates(identificationId);

  if (error && !data) {
    return (
      <Panel data-testid='part-image-narrow'>
        <PanelBody>
          <ApiErrorAlert error={getApiError(error, 'Could not load the measurements for this item type')} />
          {chosenItemType && (
            <Button variant='outlined' onClick={() => setChosenItemType(undefined)} sx={{ alignSelf: 'flex-start' }}>
              Back to the suggested item type
            </Button>
          )}
        </PanelBody>
      </Panel>
    );
  }
  if (!data) return <NarrowSkeleton />;

  const loaded = data.itemName
    ? { itemName: data.itemName, federalSupplyClass: data.federalSupplyClass ?? '' }
    : undefined;
  const current = isPlaceholderData ? chosenItemType : loaded;
  const chooseItemType = (itemType: PartImageItemTypeOption) => {
    setChosenItemType(itemType);
    setAnswers({});
    narrow.reset();
  };
  const tolerance = (TOLERANCE_CHOICES.find(choice => choice.label === toleranceLabel) ?? DEFAULT_TOLERANCE_CHOICE)
    .tolerance;
  const chooseTolerance = (label: string) => {
    setToleranceLabel(label);
    narrow.reset();
  };
  const request = { ...loaded, ...toRequest(data.questions, answers), tolerance, ...target };
  const dimensions = data.questions.filter(question => question.kind === 'dimension');
  const details = data.questions.filter(question => question.kind === 'text');
  const sectionProperties = {
    answers,
    tolerance,
    disabled: isPlaceholderData,
    onAnswer: (code: string, value: string) => setAnswers(previous => ({ ...previous, [code]: value })),
  };
  return (
    <Panel data-testid='part-image-narrow'>
      <PanelBody>
        <Stack gap='4px'>
          <Typography variant='subtitle1'>Narrow it down by measuring the part</Typography>
          {isPlaceholderData && <Skeleton variant='text' width='45%' />}
          {!isPlaceholderData && (
            <Typography variant='body2' color='text.secondary'>
              {data.itemName
                ? `${data.familySize.toLocaleString()} ${data.itemName} parts in FLIS.`
                : 'FLIS has no parts of the item types suggested from the photos. Search for the item type instead.'}
            </Typography>
          )}
        </Stack>
        <ItemTypeChoice itemTypes={data.itemTypes} current={current} onChoose={chooseItemType} />
        {isPlaceholderData && <FieldSkeletons count={3} />}
        {!isPlaceholderData && data.itemName && (
          <QuestionSection
            title='Measurements'
            caption={
              dimensions.length > 0
                ? "In inches. Give at least one; leave blank what you can't measure."
                : 'FLIS records no measurements for this item type.'
            }
            questions={dimensions}
            testId='part-image-measurements'
            control={
              dimensions.length > 0 && (
                <ToleranceChoice label={toleranceLabel} disabled={isPlaceholderData} onChoose={chooseTolerance} />
              )
            }
            {...sectionProperties}
          />
        )}
        {!isPlaceholderData && data.itemName && details.length > 0 && (
          <QuestionSection
            title='Other details'
            caption='Optional. Type a word, or pick one FLIS uses.'
            questions={details}
            testId='part-image-details'
            {...sectionProperties}
          />
        )}
        <ApiErrorAlert error={getApiError(narrow.error ?? undefined, 'Narrowing failed')} />
      </PanelBody>
      <PanelFooter>
        <Button
          variant='contained'
          fullWidth
          onClick={() => narrow.mutate(request, { onSuccess: result => onNarrowed?.(result.candidates) })}
          disabled={
            request.dimensions.length === 0 ||
            unreadableSizes(data.questions, answers) ||
            narrow.isPending ||
            isPlaceholderData
          }
          data-testid='part-image-narrow-submit'
        >
          Narrow down
        </Button>
        {narrow.data && <MatchSummary total={narrow.data.total} shown={narrow.data.candidates.length} />}
      </PanelFooter>
    </Panel>
  );
};
